import { createSelector } from '@reduxjs/toolkit'

import { ApplicationState } from 'redux/Stores/types'
import {
  TGroupApi,
  GroupMember,
  MemberStatus,
  MemberType,
  TGroup,
  SelectOneGroup
} from 'types/GlobalGroup'
import { TKeyMetric } from 'types/GlobalKeyMetric'
import { getSlicesKey } from 'utils/slices'
import { selectAvailableKeyMetrics, selectKeyMetrics } from './KeyMetrics'
import { EAccountRole } from 'types/GlobalAccount'
import { sortAlphabeticalAsc } from 'utils/sortHelper'
import {
  getBooleanStates,
  TReduxBooleanState,
  EReduxState
} from 'redux/utils/State'

const selectGroupStore = (state: ApplicationState) => state.GroupStore

export const selectIsAddingMetricToGroup = createSelector(
  [selectGroupStore],
  (store) => store.isAddingMetric
)

const isActiveMember = (accountId: string, groupMembers: GroupMember[]) => {
  return groupMembers.some(
    (member) =>
      member.status === MemberStatus.APPROVED && member.account_id === accountId
  )
}

const buildKeyMetricsTreeForGroup = (
  group: TGroupApi,
  keyMetrics: TKeyMetric[]
): TKeyMetric[] => {
  const groupKeyMetricIds = new Set(group.key_metrics)

  const filterTree = (kms: TKeyMetric[]): TKeyMetric[] =>
    kms.flatMap((km) => {
      const filteredChildren = filterTree(km.children)

      if (groupKeyMetricIds.has(km.id)) {
        const pin = group.pins.find((p) => p.key_metric_id === km.id)

        return [
          {
            ...km,
            children: filteredChildren,
            pin_comment: pin ? pin.comment : null,
            pin_id: pin ? pin.id : null,
            pinned: !!pin
          }
        ]
      }

      // Not in group — hoist any matched descendants up to this level
      return filteredChildren
    })

  return filterTree(keyMetrics)
}

// TODO: remove
export const selectAllGroupsFollowing = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.GroupStore.data,
    selectAvailableKeyMetrics,
    (state: ApplicationState, kpiTemplateId: string) => kpiTemplateId,
    (state: ApplicationState, kpiTemplateId: string, slices: string[]) => slices
  ],
  (groups, keyMetrics, kpiTemplateId, slices) => {
    const sliceKey = getSlicesKey(slices)

    return Object.values(groups)
      .map((group) => {
        const hasKeyMetric = group.key_metrics.some((kmId) => {
          return (
            keyMetrics[kmId] &&
            keyMetrics[kmId].metric.id === kpiTemplateId &&
            getSlicesKey(keyMetrics[kmId].metric.settings?.slices ?? []) ===
              sliceKey
          )
        })

        const hasMetric = group.metrics.some(({ metric }) => {
          return (
            metric.id === kpiTemplateId &&
            getSlicesKey(metric.settings?.slices ?? []) === sliceKey
          )
        })

        if (hasKeyMetric || hasMetric) {
          return { ...group, isKey: hasKeyMetric, isMetric: hasMetric }
        }

        return null
      })
      .filter(Boolean) as Array<
      TGroupApi & { isKey: boolean; isMetric: boolean }
    >
  }
)

export const selectGroups = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.AuthStore.account,
    (state: ApplicationState) => state.GroupStore.data,
    (state: ApplicationState) => state.GroupStore.state,
    selectKeyMetrics
  ],
  (
    user,
    data,
    state,
    keyMetrics
  ): {
    myGroups: TGroup[]
    otherGroups: TGroup[]
    allGroups: TGroup[]
  } & TReduxBooleanState => {
    if (!user || state !== EReduxState.SUCCESS) {
      return {
        myGroups: [],
        otherGroups: [],
        allGroups: [],
        ...getBooleanStates(state)
      }
    }

    const groups = Object.values(data)
      .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))
      .map((group) => {
        return {
          ...group,
          key_metrics: buildKeyMetricsTreeForGroup(group, keyMetrics[0])
        }
      })

    const myGroups = groups.filter((group) =>
      isActiveMember(user.id, group.members)
    )

    const otherGroups = groups.filter(
      (group) => !isActiveMember(user.id, group.members)
    )

    return {
      myGroups,
      otherGroups,
      allGroups: groups,
      ...getBooleanStates(state)
    }
  }
)

export const selectGroup = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.GroupStore.data,
    (state: ApplicationState) => state.AccountStore.data,
    selectKeyMetrics,
    (state: ApplicationState, id: string) => id
  ],
  (data, accounts, keyMetrics, id): SelectOneGroup | null => {
    const group = data[id]

    if (!group) {
      return null
    }

    const allMembers = group.members
      .map((member) => {
        const account = accounts[member.account_id]

        return {
          ...member,
          name:
            account && account.first_name
              ? `${account.first_name || ''} ${account.last_name || ''}`
              : 'Namnlös'
        }
      })
      .sort((a, b) => sortAlphabeticalAsc(a.name, b.name))

    const activeMembers = allMembers.filter(
      (member) => member.status === MemberStatus.APPROVED
    )

    return {
      ...group,
      key_metrics: buildKeyMetricsTreeForGroup(group, keyMetrics[0]),
      active_members: activeMembers,
      members: activeMembers.filter(
        (member) => member.type === MemberType.MEMBER
      ),
      owners: activeMembers.filter(
        (member) => member.type === MemberType.OWNER
      ),
      pending: allMembers.filter(
        (member) =>
          member.type === MemberType.MEMBER &&
          member.status === MemberStatus.PENDING
      )
    }
  }
)

export const selectIsOwner = createSelector(
  [
    // Input selectors:
    (state: ApplicationState) => state.AuthStore.account,
    (state: ApplicationState) => state.GroupStore.data,
    (state: ApplicationState, id: string) => id
  ],
  (user, groups, id) => {
    const group = groups[id]

    if (!user || !group) {
      return false
    }

    return (
      user.is_homepal_user ||
      user.role === EAccountRole.ADMIN ||
      group.members
        .filter((member) => member.type === MemberType.OWNER)
        .some((owner) => owner.account_id === user.id)
    )
  }
)
